import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileCheck2,
  FileSearch,
  RotateCcw,
  XCircle
} from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { PATHS, ROUTE_ACTIONS, SEGMENTS } from '@/app/router/routes'
import type {
  ApprovalFlowStatus,
  DocumentApprovalFlowResponse,
  ParticipanRespondStatus
} from '@/features/negotiations/hooks/use-document-approval-flows'
import { getFlowsByNegotiation } from '@/features/negotiations/services/approval-flow.services'
import { queryKeys } from '@/shared/constants/query-keys'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { formatHumanDate } from '@/shared/utils/format-date'

const FLOW_STATUS: Record<ApprovalFlowStatus, { label: string; className: string }> = {
  review: {
    label: 'En revisión',
    className:
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
  },
  approved: {
    label: 'Aprobado',
    className:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
  },
  rejected: {
    label: 'Rechazado',
    className:
      'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300'
  },
  cancelled: {
    label: 'Cancelado',
    className: 'border-border bg-muted text-muted-foreground'
  }
}

const PARTICIPANT_STATUS: Record<ParticipanRespondStatus, { label: string; className: string }> = {
  pending: {
    label: 'Pendiente',
    className:
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
  },
  viewed: {
    label: 'Visto',
    className:
      'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
  },
  approved: {
    label: 'Aprobado',
    className:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
  },
  rejected: {
    label: 'Rechazado',
    className:
      'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300'
  }
}

const PARTICIPANT_TYPE: Record<string, string> = {
  owner: 'Propietario',
  manager: 'Gestor',
  advisor: 'Asesor',
  guest: 'Invitado'
}

function formatDate(value: Date | string | null | undefined): string | null {
  if (!value) return null
  const formatted = formatHumanDate(value, '')
  return formatted === '' ? null : formatted
}

function FlowStatusBadge({ status }: { status: ApprovalFlowStatus }) {
  const config = FLOW_STATUS[status]
  return (
    <span
      className={cn(
        'shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold',
        config.className
      )}
    >
      {config.label}
    </span>
  )
}

function ParticipantStatusBadge({ status }: { status: ParticipanRespondStatus }) {
  const config = PARTICIPANT_STATUS[status]
  return (
    <span
      className={cn(
        'shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  )
}

function ApprovalFlowCardContent({ flow }: { flow: DocumentApprovalFlowResponse }) {
  const total = flow.totalParticipants || flow.participants.length
  const approved = flow.approvedCount ?? 0
  const percentage = total > 0 ? Math.min(100, Math.round((approved / total) * 100)) : 0
  const expiresAt = formatDate(flow.expiresAt)
  const documentsLabel =
    flow.selectedDocuments.length === 1
      ? '1 documento'
      : `${flow.selectedDocuments.length} documentos`
  const maxVisibleParticipants = 3
  const hiddenParticipants = Math.max(0, flow.participants.length - maxVisibleParticipants)

  return (
    <>
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <h4 className="min-w-0 flex-1 truncate text-sm font-semibold" title={flow.name}>
              {flow.name}
            </h4>
            <FileSearch
              aria-hidden="true"
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
            />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Versión {flow.version} · {documentsLabel}
          </p>
        </div>
        <FlowStatusBadge status={flow.status} />
      </div>

      {flow.integrityWarnings && flow.integrityWarnings.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-800 dark:bg-amber-950/40">
          <div className="flex items-center gap-1.5">
            <AlertTriangle
              aria-hidden="true"
              className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
            />
            <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">
              Revisar firmas del documento
            </span>
          </div>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-6 text-[11px] leading-snug text-amber-700 dark:text-amber-300/90">
            {flow.integrityWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground">Aprobación</span>
          <span className="font-medium tabular-nums">
            {approved} de {total}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label={`Aprobación de ${flow.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percentage}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full rounded-full bg-primary" style={{ width: `${percentage}%` }} />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded-md bg-emerald-50 px-1.5 py-1.5 dark:bg-emerald-950/30">
          <CheckCircle2 aria-hidden="true" className="mx-auto h-3.5 w-3.5 text-emerald-600" />
          <p className="mt-0.5 text-[10px] text-muted-foreground">Aprobados</p>
          <p className="text-xs font-semibold tabular-nums">{flow.approvedCount ?? 0}</p>
        </div>
        <div className="rounded-md bg-amber-50 px-1.5 py-1.5 dark:bg-amber-950/30">
          <Clock3 aria-hidden="true" className="mx-auto h-3.5 w-3.5 text-amber-600" />
          <p className="mt-0.5 text-[10px] text-muted-foreground">Pendientes</p>
          <p className="text-xs font-semibold tabular-nums">{flow.pendingCount ?? 0}</p>
        </div>
        <div className="rounded-md bg-red-50 px-1.5 py-1.5 dark:bg-red-950/30">
          <XCircle aria-hidden="true" className="mx-auto h-3.5 w-3.5 text-red-600" />
          <p className="mt-0.5 text-[10px] text-muted-foreground">Rechazados</p>
          <p className="text-xs font-semibold tabular-nums">{flow.rejectedCount ?? 0}</p>
        </div>
      </div>

      <div className="mt-3 border-t pt-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold">Personas vinculadas</span>
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {flow.participants.length}
          </span>
        </div>

        <div className="space-y-2">
          {flow.participants.slice(0, maxVisibleParticipants).map((participant) => (
            <div key={participant.id} className="flex min-w-0 items-center gap-2">
              <Avatar seed={participant.id} className="h-8 w-8 shrink-0">
                <AvatarImage
                  src={participant.avatar ?? undefined}
                  alt={participant.name || participant.email}
                  className="object-cover"
                />
                <AvatarFallback className="text-[9px] uppercase">
                  {participant.name?.[0] ?? participant.email?.[0] ?? '?'}
                </AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">
                  {participant.name || participant.email}
                </p>
                <p className="truncate text-[10px] text-muted-foreground">
                  {PARTICIPANT_TYPE[participant.type] ?? 'Participante'} ·{' '}
                  {participant.approval === 'required' ? 'Aprobación requerida' : 'Opcional'}
                </p>
              </div>

              <ParticipantStatusBadge status={participant.status} />
            </div>
          ))}

          {hiddenParticipants > 0 ? (
            <p className="pl-10 text-[11px] text-muted-foreground">
              +{hiddenParticipants} {hiddenParticipants === 1 ? 'persona más' : 'personas más'}
            </p>
          ) : null}
        </div>

        {expiresAt ? (
          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <CalendarClock aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Vence {expiresAt}</span>
          </p>
        ) : null}
      </div>
    </>
  )
}

function ApprovalFlowCard({
  flow,
  negotiationId,
  onNavigate
}: {
  flow: DocumentApprovalFlowResponse
  negotiationId: string
  onNavigate: () => void
}) {
  const destination = `${PATHS.admin.negotiations.step(
    ROUTE_ACTIONS.view,
    negotiationId,
    SEGMENTS.negotiations.print
  )}?flowId=${encodeURIComponent(flow._id)}`

  return (
    <Link
      to={destination}
      onClick={onNavigate}
      aria-label={`Ver aprobación ${flow.name}`}
      className="group block rounded-lg border p-3 transition-colors hover:border-primary/30 hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <ApprovalFlowCardContent flow={flow} />
    </Link>
  )
}

/**
 * Los flujos de aprobación de un expediente.
 *
 * Recibe el `neg_` —el único id que v2 publica— desde que la decisión Nº 16 habilitó que
 * `document-approval-flows` lo traduzca. Acepta `null` porque el tipo del detalle declara
 * `publicId` opcional: el backfill ya corrió en los tres entornos, así que un expediente sin él
 * sería un dato imposible, pero **la pestaña no es el lugar para reventar por eso** — se queda
 * quieta en vez de disparar una consulta que no puede resolver.
 */
export function NegotiationApprovalsTab({ negotiationId }: { negotiationId: string | null }) {
  const closePanel = useSidePanelStore((state) => state.closePanel)
  const {
    data: flows = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: queryKeys.approvalFlows.detail(negotiationId ?? '', 'negotiation-summary'),
    queryFn: async () => await getFlowsByNegotiation(negotiationId ?? ''),
    enabled: Boolean(negotiationId),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  })
  const orderedFlows = useMemo(
    () => [...flows].sort((left, right) => +new Date(right.updatedAt) - +new Date(left.updatedAt)),
    [flows]
  )

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-center">
        <p className="text-sm font-medium">No se pudieron cargar las aprobaciones</p>
        <p className="mt-1 text-xs text-muted-foreground">Intentá nuevamente.</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => {
            void refetch()
          }}
        >
          <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
          Reintentar
        </Button>
      </div>
    )
  }

  /*
   * Sin `neg_` no hay nada que pedir ni a dónde navegar, y se muestra el mismo vacío que cuando no
   * hay flujos. El estrechamiento es además lo que le permite al card seguir pidiendo un `string`
   * de verdad: la alternativa —pasarle `negotiationId ?? ''`— construiría un enlace roto en vez de
   * no construirlo, que es el tipo de caída silenciosa que este repo viene sacando.
   */
  if (!negotiationId || orderedFlows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <FileCheck2 aria-hidden="true" className="mx-auto h-5 w-5 text-muted-foreground" />
        <p className="mt-2 text-sm font-medium">Sin aprobaciones de documentos</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Todavía no se ha compartido ningún documento para aprobación.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {orderedFlows.map((flow) => (
        <ApprovalFlowCard
          key={flow._id}
          flow={flow}
          negotiationId={negotiationId}
          onNavigate={closePanel}
        />
      ))}
    </div>
  )
}
